/**
 * @fileoverview DOM Utilities - Core DOM manipulation and query helpers
 * @version 0.1.0
 * @license MIT
 * 
 * Provides utility functions for DOM operations including element queries,
 * shadow DOM support, element creation, and viewport checks.
 * 
 * USAGE:
 * ```javascript
 * import { createElement, isInViewport, shadowQuery } from '@cliftontechlabs/ui-kit/utils/core';
 * 
 * const button = createElement('button', { class: 'btn' }, 'Click me');
 * const isVisible = isInViewport(element);
 * ```
 * 
 * @example
 * // Create element with event listeners
 * const button = createElement('button', {
 *   class: 'btn-primary',
 *   onclick: () => console.log('clicked')
 * }, 'Submit');
 */

/**
 * Find the closest ancestor element matching a selector (including the element itself)
 * @param {Element} element - Starting element
 * @param {string} selector - CSS selector to match
 * @returns {Element|null} Matching element or null
 * 
 * @example
 * const card = closest(button, '.card');
 */
export function closest(element, selector) {
  if (!element) return null;
  if (element.matches(selector)) return element;
  return closest(element.parentElement, selector);
}

/**
 * Query within a shadow root
 * @param {HTMLElement} host - Element with shadow root
 * @param {string} selector - CSS selector
 * @returns {Element|null}
 */
export function shadowQuery(host, selector) {
  return host.shadowRoot?.querySelector(selector) || null;
}

/**
 * Query all within a shadow root
 * @param {HTMLElement} host - Element with shadow root
 * @param {string} selector - CSS selector
 * @returns {NodeList}
 */
export function shadowQueryAll(host, selector) {
  return host.shadowRoot?.querySelectorAll(selector) || [];
}

/**
 * Create an element with optional attributes and children
 * @param {string} tagName - HTML tag name (e.g., 'div', 'button')
 * @param {Object} [attrs={}] - Key-value pairs of attributes
 * @param {...(Node|string)} children - Child nodes or strings to append
 * @returns {Element} Created element
 * 
 * @example
 * const div = createElement('div', { class: 'container' },
 *   createElement('h1', {}, 'Title'),
 *   createElement('p', {}, 'Content')
 * );
 * 
 * @example
 * const button = createElement('button', {
 *   class: 'btn',
 *   onclick: () => alert('Clicked!'),
 *   'data-id': '123'
 * }, 'Click Me');
 */
export function createElement(tagName, attrs = {}, ...children) {
  const el = document.createElement(tagName);

  for (const [key, value] of Object.entries(attrs)) {
    if (key === 'class') {
      el.className = value;
    } else if (key === 'style') {
      el.style.cssText = value;
    } else if (key.startsWith('on') && typeof value === 'function') {
      el.addEventListener(key.slice(2), value);
    } else {
      el.setAttribute(key, value);
    }
  }

  for (const child of children) {
    if (typeof child === 'string') {
      el.appendChild(document.createTextNode(child));
    } else if (child instanceof Node) {
      el.appendChild(child);
    }
  }

  return el;
}

/**
 * Toggle a class on an element
 * @param {Element} element
 * @param {string} className
 * @param {boolean} [force] - true to add, false to remove
 */
export function toggleClass(element, className, force) {
  element.classList.toggle(className, force);
}

/**
 * Check if element is visible in the viewport
 * @param {Element} element
 * @returns {boolean}
 */
export function isInViewport(element) {
  if (!element) return false;
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}
